import { useEffect, useRef } from "react";
import Hls from "hls.js";
import { fireComscoreBeacon } from "@/utils/comscoreBeacon";

interface UseComscoreHLSTrackingParams {
  hlsRef: React.MutableRefObject<Hls | null>;
  contentType: string;
  language: string;
  c5Code: string;
  contentSlug: string;
  enabled?: boolean;
  reinitTrigger?: unknown;
}

export function useComscoreHLSTracking({
  hlsRef,
  contentType,
  language,
  c5Code,
  contentSlug,
  enabled = true,
  reinitTrigger,
}: UseComscoreHLSTrackingParams): void {
  const totalSegmentsRef = useRef<number>(0);

  useEffect(() => {
    if (!enabled) return;

    const hls = hlsRef.current;
    if (!hls) return;

    const handleLevelSwitched = (_event: string, data: any) => {
      if (hls.levels && hls.levels.length > 0 && data.level !== undefined) {
        const newLevel = hls.levels[data.level];
        if (newLevel?.details?.fragments) {
          totalSegmentsRef.current = newLevel.details.fragments.length;
        }
      }
    };

    const handleFragChanged = (_event: string, data: any) => {
      if (
        data.frag &&
        typeof data.frag.sn === "number" &&
        totalSegmentsRef.current > 0
      ) {
        fireComscoreBeacon({
          contentType,
          language,
          c5Code,
          contentSlug,
          segmentIndex: data.frag.sn + 1,
          totalSegments: totalSegmentsRef.current,
        });
      }
    };

    hls.on(Hls.Events.LEVEL_SWITCHED, handleLevelSwitched);
    hls.on(Hls.Events.FRAG_CHANGED, handleFragChanged);

    // Pick up initial total segments if manifest is already loaded
    if (hls.levels && hls.levels.length > 0) {
      const currentLevel =
        hls.levels[hls.currentLevel !== -1 ? hls.currentLevel : 0];
      if (currentLevel?.details?.fragments) {
        totalSegmentsRef.current = currentLevel.details.fragments.length;
      }
    }

    return () => {
      hls.off(Hls.Events.LEVEL_SWITCHED, handleLevelSwitched);
      hls.off(Hls.Events.FRAG_CHANGED, handleFragChanged);
    };
  }, [enabled, contentType, language, c5Code, contentSlug, reinitTrigger]);
}
